/**
 * Publish summary modal — shows the operator exactly what will change
 * when they click Publish. Helps prevent accidental publishes.
 */
import type {EditorSnapshot} from '../../lib/types';

interface Props {
  snapshot: EditorSnapshot;
  onConfirm: () => void;
  onClose: () => void;
  publishing: boolean;
}

function diffSections(
  before: EditorSnapshot['pages'][number]['sections'],
  after: EditorSnapshot['pages'][number]['sections'],
): {added: string[]; removed: string[]; changed: {id: string; type: string; changes: string[]}[]} {
  const beforeById = new Map(before.map((s) => [s.id, s]));
  const afterById = new Map(after.map((s) => [s.id, s]));
  const added: string[] = [];
  const removed: string[] = [];
  const changed: {id: string; type: string; changes: string[]}[] = [];
  for (const [id, s] of afterById) {
    if (!beforeById.has(id)) {
      added.push(`${s.type} (${id.slice(0, 6)})`);
    } else {
      const b = beforeById.get(id)!;
      const ch: string[] = [];
      if (b.enabled !== s.enabled) ch.push(s.enabled ? '启用' : '停用');
      if (JSON.stringify(b.data) !== JSON.stringify(s.data)) ch.push('内容');
      if (b.sort_order !== s.sort_order) ch.push('顺序');
      if (ch.length > 0) {
        changed.push({id, type: s.type, changes: ch});
      }
    }
  }
  for (const [id, s] of beforeById) {
    if (!afterById.has(id)) {
      removed.push(`${s.type} (${id.slice(0, 6)})`);
    }
  }
  return {added, removed, changed};
}

export default function PublishSummaryModal({snapshot, onConfirm, onClose, publishing}: Props) {
  const draft = snapshot;
  const published = snapshot.published_revision;

  const sectionChanges: ReturnType<typeof diffSections> = {
    added: [],
    removed: [],
    changed: [],
  };
  for (const p of draft.pages) {
    const s = diffSections([], p.sections);
    sectionChanges.added.push(...s.added);
    sectionChanges.changed.push(...s.changed);
  }

  const brandingFields: [string, unknown][] = Object.entries(draft.branding as Record<string, unknown> || {});
  const hasBranding = brandingFields.length > 0;
  const navItems = ((draft.navigation as {items?: unknown[]} | undefined)?.items || []) as Array<{
    id: string;
    label: string;
    page_slug?: string | null;
    url?: string | null;
  }>;
  const hasNav = navItems.length > 0;
  const footerFields: [string, unknown][] = Object.entries(draft.footer as Record<string, unknown> || {});
  const hasFooter = footerFields.length > 0;

  const isDirty = draft.draft_revision > published;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>本次发布内容</h3>
          <button className="icon-btn" onClick={onClose} title="关闭">×</button>
        </div>

        {!isDirty && (
          <div className="editor-info">
            自上次发布以来没有修改，无需发布。
          </div>
        )}

        {isDirty && (
          <div className="publish-summary">
            <p>
              当前 <strong>草稿 v{draft.draft_revision}</strong> 将被发布为 <strong>已发布 v{draft.draft_revision}</strong>。
              最近一次发布是 <strong>v{published}</strong>。
            </p>

            {hasBranding && (
              <section>
                <h4>品牌设置</h4>
                <ul>
                  {brandingFields.map(([k, v]) => (
                    <li key={k}><code>{k}</code> = <code>{JSON.stringify(v)}</code></li>
                  ))}
                </ul>
              </section>
            )}

            {hasNav && (
              <section>
                <h4>导航菜单（{navItems.length} 项）</h4>
                <ul>
                  {navItems.map((it) => (
                    <li key={it.id}>
                      <code>{it.label}</code>
                      {it.page_slug && <> → /{it.page_slug}</>}
                      {it.url && !it.page_slug && <> → {it.url}</>}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {hasFooter && (
              <section>
                <h4>页脚</h4>
                <ul>
                  {footerFields.map(([k, v]) => {
                    if (Array.isArray(v)) {
                      return <li key={k}><code>{k}</code>：{v.length} 项</li>;
                    }
                    if (typeof v === 'object' && v !== null) {
                      return <li key={k}><code>{k}</code>：{Object.keys(v).length} 个键</li>;
                    }
                    return <li key={k}><code>{k}</code> = <code>{JSON.stringify(v)}</code></li>;
                  })}
                </ul>
              </section>
            )}

            {sectionChanges.added.length > 0 && (
              <section>
                <h4>新增区块</h4>
                <ul>
                  {sectionChanges.added.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </section>
            )}

            {sectionChanges.changed.length > 0 && (
              <section>
                <h4>修改区块</h4>
                <ul>
                  {sectionChanges.changed.map((c) => (
                    <li key={c.id}>
                      <code>{c.type}</code> ({c.id.slice(0, 6)})：{c.changes.join('、')}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {sectionChanges.removed.length > 0 && (
              <section>
                <h4>删除区块</h4>
                <ul>
                  {sectionChanges.removed.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </section>
            )}

            <p className="publish-warning">
              <strong>注意：</strong>发布完成后线上站点会立即更新。如果不确定效果，可以先点「预览」查看。
            </p>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={publishing}>
            取消
          </button>
          <button
            className="btn-primary"
            onClick={onConfirm}
            disabled={!isDirty || publishing}
          >
            {publishing ? '发布中…' : '确认发布'}
          </button>
        </div>
      </div>
    </div>
  );
}
